/**
 * Log levels
 */
export const LEVELS = {
  trace: 1,
  debug: 2,
  info: 3,
  log: 4,
  warn: 5,
  error: 6,
  silent: 100,
}

type LevelsKeys = keyof typeof LEVELS
type LevelsValues = (typeof LEVELS)[LevelsKeys]
type LevelsMap = Map<string, LevelsValues>

/**
 * Compiled mask entry: a glob pattern converted to a RegExp plus its level.
 *
 * Supported syntax:
 *   `* /analytix=trace`  – any logger whose path ends with "/analytix"
 *   `app/*=debug`        – any logger under "app" at any depth
 *
 * Rules:
 *  - `*` matches any sequence of characters, including slashes (any depth)
 *  - A mask is only used when no exact-name / parent-walk match was found first.
 */
type MaskEntry = { re: RegExp; level: LevelsValues }

/**
 * Log level `localStorage` persist key
 */
const LOG_PERSIST_KEY = 'tria__log'

/**
 * Default log level
 */
const DEFAULT_LOG_LEVEL =
  __NODE_ENV__ === 'production' || __NODE_ENV__ === 'test' ? 'error' : 'trace'

/**
 * Log level map
 */
let log: LevelsMap = parse(get())

/** Mask list derived from the same parse() call */
let masks: MaskEntry[] = buildMasks(log)

// ---------------------------------------------------------------------------
// Mask helpers
// ---------------------------------------------------------------------------

/**
 * Returns true when a name token looks like a glob (contains *).
 */
function isMask(name: string): boolean {
  return name.includes('*')
}

/**
 * Convert a glob-style mask string to a RegExp.
 *
 * `*` → matches any sequence of characters including slashes (any depth)
 * everything else is escaped.
 */
function maskToRegExp(mask: string): RegExp {
  const src = mask
    .split('*')
    .map((s) => s.replace(/[.+^${}()|[\]\\]/g, '\\$&'))
    .join('.*')

  return new RegExp('^' + src + '$')
}

/**
 * Build the ordered mask list from a parsed LevelsMap.
 * Masks are stored in insertion order so more specific (later) entries win
 * when multiple masks match — last match wins, consistent with CSS-like cascade.
 */
function buildMasks(map: LevelsMap): MaskEntry[] {
  const result: MaskEntry[] = []
  for (const [name, level] of map.entries()) {
    if (isMask(name)) {
      result.push({ re: maskToRegExp(name), level })
    }
  }
  return result
}

/**
 * Find the last matching mask level for a given logger name.
 * Returns `undefined` when no mask matches.
 */
function matchMasks(logger: string): LevelsValues | undefined {
  let matched: LevelsValues | undefined
  for (const { re, level } of masks) {
    if (re.test(logger)) {
      matched = level // last match wins
    }
  }
  return matched
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Log level check function.
 *
 * Priority (highest → lowest):
 *  1. Exact logger name in the LOG map
 *  2. Nearest ancestor in the LOG map  (parent walk)
 *  3. First matching glob mask in the LOG map
 *  4. Constructor-supplied `def` level
 *  5. Global default (empty-string key in LOG map, or DEFAULT_LOG_LEVEL)
 *
 * Returns a function that accepts a level value and returns `true` when the
 * logger should emit messages at that level.
 */
export const check = (logger: string, def?: LevelsValues) => {
  const DEF = LEVELS[DEFAULT_LOG_LEVEL as LevelsKeys]

  // Collect the logger and all its ancestors for the parent-walk lookup.
  const loggers: string[] = []
  let cur = logger
  do {
    loggers.push(cur)
    const idx = cur.lastIndexOf('/')
    if (idx < 0) break
    cur = cur.slice(0, idx)
  } while (cur)

  return (lvl: LevelsValues): boolean => {
    // 1 & 2: exact match or nearest ancestor
    let level: LevelsValues | undefined
    for (const name of loggers) {
      level = log.get(name)
      if (level !== undefined) break
    }

    // 3: glob mask fallback
    if (level === undefined) {
      level = matchMasks(logger)
    }

    // 4 & 5: constructor default → global default
    return (level ?? def ?? log.get('') ?? DEF) <= lvl
  }
}

// ---------------------------------------------------------------------------
// localStorage helpers
// ---------------------------------------------------------------------------

function get(): string | null | undefined {
  try {
    return localStorage.getItem(LOG_PERSIST_KEY)
  } catch (ignore) {
    // ignore errors
  }
}

function set(level?: string | null): void {
  try {
    if (level == null) {
      localStorage.removeItem(LOG_PERSIST_KEY)
    } else {
      localStorage.setItem(LOG_PERSIST_KEY, level)
    }
  } catch (ignore) {
    // ignore errors
  }
}

// ---------------------------------------------------------------------------
// Parse / stringify
// ---------------------------------------------------------------------------

/**
 * Parse log level string into a map of { logger => level }.
 * Mask entries (those containing * or ?) are stored in the map too;
 * `buildMasks()` extracts them into a fast-match list.
 */
function parse(log?: string | null): LevelsMap {
  log = log || DEFAULT_LOG_LEVEL

  const map: LevelsMap = new Map()
  for (const logger of log.split(',')) {
    let [name, level] = logger.split('=', 2)
    if (!name) continue
    if (!level) {
      level = name
      name = ''
    }
    if (level in LEVELS) {
      map.set(name, LEVELS[level as LevelsKeys])
    }
  }

  if (map.size === 0) {
    map.set('', LEVELS[DEFAULT_LOG_LEVEL as LevelsKeys])
  }

  return map
}

/**
 * Stringify log level map back into a `LOG`-style string.
 */
function stringify(map: LevelsMap): string {
  const strs: string[] = []
  for (const [name, level] of map.entries()) {
    const lvl = Object.keys(LEVELS).find(
      (key) => LEVELS[key as LevelsKeys] === level
    )
    if (!lvl) continue
    strs.push(name ? name + '=' + lvl : lvl)
  }
  return strs.join(',')
}

// ---------------------------------------------------------------------------
// Global LOG devtools hook
// ---------------------------------------------------------------------------

Object.defineProperty(globalThis, 'LOG', {
  get: () => stringify(log),
  set: (value?: string | null) => {
    log = parse(value)
    masks = buildMasks(log)
    const str = stringify(log)
    set(str === DEFAULT_LOG_LEVEL ? undefined : str)
  },
})
